import CompanyCard from '@/components/CompanyCard';
import Header from '@/components/Header';
import { HomeCityFilters } from '@/components/HomeCityFilters';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Slider } from '@/components/ui/slider';
import { companyQueryOptions } from '@/hooks/queries/company.queries';
import { useCitiesWithCompanies } from '@/hooks/queries/useCitiesWithCompanies';
import { useCompaniesList } from '@/hooks/queries/useCompaniesList';
import { useFavorites } from '@/hooks/useFavorites';
import { useGeolocation } from '@/hooks/useGeolocation';
import { buildCompaniesQuery, ITEMS_PER_PAGE } from '@/lib/company/build-companies-query';
import {
  buildCompanyStatusBadge,
  resolveCompanyOpenStatus,
  shouldFetchCompanyDetailsForCard,
} from '@/lib/company/status';
import { buildFavoriteShopSnapshot } from '@/lib/favorites';
import { useQueries } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Compass, MapPin, Search, SlidersHorizontal } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

const Index = () => {
  const {
    search,
    page,
    radiusDraft,
    showRadius,
    setShowRadius,
    selectedCity,
    resultsSectionRef,
    geo,
    isFavorite,
    toggleFavorite,
    companies,
    pagination,
    isLoading,
    isError,
    cities,
    companyCards,
    totalPages,
    resultCountLabel,
    canGoPrevious,
    canGoNext,
    handleSearchChange,
    handleCitySelection,
    handleRadiusChange,
    handleRadiusCommit,
    handlePageChange,
  } = useCompanySearchModel();
  return (
    <div className="bg-background min-h-screen">
      <Header showSearch searchValue={search} onSearchChange={handleSearchChange} />

      {/* Hero */}
      <section className="container pt-8 pb-6 md:pt-14 md:pb-10">
        <h1 className="text-foreground text-3xl leading-tight font-bold md:text-4xl">
          Encontre um horário
          <br />
          <span className="text-primary">perto de você</span>
        </h1>
        <p className="text-muted-foreground mt-2 max-w-md text-base md:text-lg">
          Escolha um serviço, veja os horários e agende.
        </p>

        {/* Mobile search */}
        <div className="relative mt-5 md:hidden">
          <Search className="text-muted-foreground absolute top-1/2 left-4 h-5 w-5 -translate-y-1/2" />
          <input
            type="text"
            aria-label="Buscar estabelecimento"
            placeholder="Buscar estabelecimento"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="bg-muted text-foreground placeholder:text-muted-foreground focus:ring-primary/30 h-12 w-full rounded-xl border-none pr-4 pl-12 text-base transition-[color,background-color,border-color,box-shadow,opacity,transform,translate] outline-none focus:ring-2"
          />
        </div>
      </section>
      {/* Location prompt / status */}
      {!geo.granted && !search && (
        <section className="container pb-6">
          <button
            type="button"
            onClick={geo.requestLocation}
            disabled={geo.loading}
            className="border-primary/20 bg-primary-light hover:border-primary/40 flex w-full items-center gap-3 rounded-xl border p-4 text-left transition-colors disabled:opacity-60"
          >
            <div className="bg-primary/10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
              <Compass className={`text-primary h-5 w-5 ${geo.loading ? 'animate-spin' : ''}`} />
            </div>
            <div>
              <p className="text-foreground text-sm font-semibold">
                {geo.loading ? 'Buscando localização...' : 'Ativar localização'}
              </p>
              <p className="text-muted-foreground text-xs">Veja os estabelecimentos mais próximos</p>
            </div>
            <MapPin className="text-primary ml-auto h-5 w-5 shrink-0" />
          </button>
        </section>
      )}
      {/* Location granted: radius control */}
      {geo.granted && (
        <section className="container pb-6">
          <div className="border-primary/20 bg-primary-light space-y-3 rounded-xl border p-4">
            <button
              type="button"
              onClick={() => setShowRadius(!showRadius)}
              className="flex w-full items-center gap-3 text-left"
            >
              <div className="bg-primary/10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
                <MapPin className="text-primary h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-foreground text-sm font-semibold">Localização ativada</p>
                <p className="text-muted-foreground text-xs">Distância máxima: {radiusDraft} km</p>
              </div>
              <SlidersHorizontal className="text-primary h-5 w-5 shrink-0" />
            </button>

            {showRadius && (
              <div className="space-y-2 pt-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Distância máxima</span>
                  <span className="text-foreground font-semibold">{radiusDraft} km</span>
                </div>
                <Slider
                  value={[radiusDraft]}
                  onValueChange={handleRadiusChange}
                  onValueCommit={handleRadiusCommit}
                  min={1}
                  max={50}
                  step={1}
                  className="w-full"
                />
                <div className="text-muted-foreground flex justify-between text-xs">
                  <span>1 km</span>
                  <span>50 km</span>
                </div>
              </div>
            )}
          </div>
        </section>
      )}
      {/* Popular cities */}
      {!search && (
        <HomeCityFilters
          cities={cities}
          isLoading={false}
          selectedCityId={selectedCity}
          onSelectCity={handleCitySelection}
        />
      )}
      {/* Listing */}
      <section ref={resultsSectionRef} className="container pb-20">
        <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-foreground min-w-0 text-lg font-bold wrap-anywhere">
            {search ? `Resultados para "${search}"` : 'Explore os estabelecimentos'}
          </h2>
          {resultCountLabel && <span className="text-muted-foreground text-sm">{resultCountLabel}</span>}
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="border-border overflow-hidden rounded-xl border">
                <Skeleton className="aspect-16/10 w-full" />
                <div className="space-y-2 p-4">
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : isError ? (
          <div className="py-16 text-center">
            <p className="text-muted-foreground font-medium">Erro ao carregar estabelecimentos</p>
            <p className="text-muted-foreground mt-1 text-sm">Tente novamente mais tarde</p>
          </div>
        ) : companies.length === 0 ? (
          <div className="py-16 text-center">
            <Search className="text-muted-foreground/30 mx-auto mb-4 h-12 w-12" />
            <p className="text-muted-foreground font-medium">Nenhum estabelecimento encontrado</p>
            <p className="text-muted-foreground mt-1 text-sm">Tente outro nome ou cidade</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {companyCards.map(({ company, statusBadge }) => (
                <CompanyCard
                  key={company.id}
                  company={company}
                  isFavorite={isFavorite(company.id)}
                  onToggleFavorite={() => toggleFavorite(buildFavoriteShopSnapshot(company))}
                  statusBadge={statusBadge}
                />
              ))}
            </div>

            {/* Pagination */}
            {pagination && totalPages > 1 && (
              <div className="mt-8 space-y-3">
                <p className="text-muted-foreground text-center text-sm">
                  Página {page} de {totalPages}
                </p>

                <div className="flex flex-wrap items-center justify-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handlePageChange(page - 1)}
                    disabled={!canGoPrevious}
                    className="rounded-xl"
                    aria-label="Ir para a página anterior"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Anterior
                  </Button>

                  <div className="flex flex-wrap items-center justify-center gap-1">
                    {generatePageNumbers(page, totalPages).map((pageNumber, index) =>
                      pageNumber === '...' ? (
                        <span
                          key={`ellipsis-${index}`}
                          aria-hidden="true"
                          className="text-muted-foreground flex h-10 w-10 items-center justify-center text-sm"
                        >
                          ...
                        </span>
                      ) : (
                        <Button
                          key={pageNumber}
                          type="button"
                          variant={page === pageNumber ? 'default' : 'outline'}
                          size="icon"
                          onClick={() => handlePageChange(pageNumber)}
                          className="rounded-xl"
                          aria-label={`Ir para a página ${pageNumber}`}
                          aria-current={page === pageNumber ? 'page' : undefined}
                        >
                          {pageNumber}
                        </Button>
                      ),
                    )}
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handlePageChange(page + 1)}
                    disabled={!canGoNext}
                    className="rounded-xl"
                    aria-label="Ir para a próxima página"
                  >
                    Próxima
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};

function generatePageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);

  const pages: (number | '...')[] = [1];

  if (current > 3) pages.push('...');

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  for (let i = start; i <= end; i++) pages.push(i);

  if (current < total - 2) pages.push('...');

  pages.push(total);
  return pages;
}

export default Index;

function useCompanySearchModel() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [radiusDraft, setRadiusDraft] = useState(10);
  const [appliedRadius, setAppliedRadius] = useState(10);
  const [showRadius, setShowRadius] = useState(false);
  const [selectedCity, setSelectedCity] = useState<string | null>(null);
  const [currentDateTime, setCurrentDateTime] = useState(() => new Date());
  const resultsSectionRef = useRef<HTMLElement | null>(null);
  const geo = useGeolocation();
  const { isFavorite, syncFavoriteItems, toggleFavorite } = useFavorites();
  const query = useMemo(
    () =>
      buildCompaniesQuery({
        appliedRadius,
        geo: {
          granted: geo.granted,
          lat: geo.lat,
          lng: geo.lng,
        },
        page,
        search,
        selectedCity,
      }),
    [appliedRadius, geo.granted, geo.lat, geo.lng, page, search, selectedCity],
  );
  const { companies, pagination, isLoading, isError } = useCompaniesList(query);
  const { cities } = useCitiesWithCompanies();
  const companyDetailsQueries = useQueries({
    queries: companies.map((company) => ({
      ...companyQueryOptions.bySlug(company.slug),
      enabled: shouldFetchCompanyDetailsForCard(company),
    })),
  });
  useEffect(() => {
    syncFavoriteItems(companies.map((company) => buildFavoriteShopSnapshot(company)));
  }, [companies, syncFavoriteItems]);
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentDateTime(new Date());
    }, 60000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);
  const companyCards = useMemo(
    () =>
      companies.map((company, index) => {
        const companyDetails = companyDetailsQueries[index]?.data;
        const workingHours = company.workingHours ?? companyDetails?.workingHours;
        const openStatus = resolveCompanyOpenStatus(company, workingHours, currentDateTime);

        return {
          company,
          statusBadge: buildCompanyStatusBadge(company, openStatus),
        };
      }),
    [companies, companyDetailsQueries, currentDateTime],
  );
  const pageSize = pagination?.take ?? ITEMS_PER_PAGE;
  const totalPages = pagination ? Math.max(1, Math.ceil(pagination.total / pageSize)) : 1;
  const resultCountLabel = pagination
    ? pagination.total === 0
      ? '0 resultados'
      : `Mostrando ${pagination.skip + 1}-${pagination.skip + pagination.count} de ${pagination.total}`
    : null;
  const canGoPrevious = page > 1;
  const canGoNext = Boolean(pagination?.hasMore) && page < totalPages;
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };
  const handleCitySelection = (value: string | null) => {
    setSelectedCity(value);
    setPage(1);
  };
  const handleRadiusChange = ([nextRadius]: number[]) => {
    if (typeof nextRadius !== 'number') return;

    setRadiusDraft(nextRadius);
  };
  const handleRadiusCommit = ([nextRadius]: number[]) => {
    if (typeof nextRadius !== 'number') return;

    setRadiusDraft(nextRadius);

    if (nextRadius === appliedRadius) return;

    setAppliedRadius(nextRadius);
    setPage(1);
  };
  const handlePageChange = (nextPage: number) => {
    const clampedPage = Math.min(Math.max(nextPage, 1), totalPages);

    if (clampedPage === page) return;

    setPage(clampedPage);
    resultsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return {
    search,
    page,
    radiusDraft,
    showRadius,
    setShowRadius,
    selectedCity,
    resultsSectionRef,
    geo,
    isFavorite,
    toggleFavorite,
    companies,
    pagination,
    isLoading,
    isError,
    cities,
    companyCards,
    totalPages,
    resultCountLabel,
    canGoPrevious,
    canGoNext,
    handleSearchChange,
    handleCitySelection,
    handleRadiusChange,
    handleRadiusCommit,
    handlePageChange,
  };
}
